import React, { ReactNode } from 'react';
import {
  Col,
  Form,
  FormItemProps,
  Input,
  InputProps,
  Row,
  Typography,
} from 'antd';
import { useController, UseControllerProps } from 'react-hook-form';

import { REGEX } from 'src/constants/app';

type Props<T> = {
  inputProps?: InputProps;
  controller: UseControllerProps<T>;
  errorCol?: 12 | 24;
  isNegativeNumber?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
} & Omit<FormItemProps, 'children'>;

function NegativeNumberField<T extends Record<string, any>>(props: Props<T>) {
  const {
    inputProps,
    controller,
    errorCol = 24,
    label,
    prefix,
    suffix,
    ...rest
  } = props;
  const { fieldState, field } = useController<T>(controller);
  const { error } = fieldState;

  const regex = REGEX.REGEX_NEGATIVE_NUMBER;

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // fix maxLength not work with japanese half-width characters
    if (inputProps?.maxLength && value.length > inputProps?.maxLength) {
      return;
    }

    if (
      !isNaN(Number(value)) ||
      regex.test(value) ||
      value === '' ||
      value === '-'
    ) {
      field.onChange(value);
    } else {
      !field.value && field.onChange('');
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!regex.test(e.clipboardData.getData('Text'))) {
      e.preventDefault();
    }
  };

  return (
    <Form.Item
      colon={false}
      label={label}
      labelAlign="left"
      labelCol={label ? { span: 24 } : undefined}
      wrapperCol={label ? { span: 24 } : undefined}
      validateStatus={error ? 'error' : ''}
      {...rest}
    >
      <Row gutter={4}>
        <Col span={errorCol}>
          <Row gutter={8}>
            <Col span={24} className="number_only_field">
              {prefix && (
                <Typography.Text style={{ marginRight: '6px' }}>
                  {prefix}
                </Typography.Text>
              )}
              <Input
                {...inputProps}
                {...field}
                onChange={onChangeInput}
                onPaste={onPaste}
                onBlur={inputProps?.onBlur}
                style={{ width: '100%' }}
              />
              {suffix && (
                <Typography.Text style={{ marginLeft: '6px' }}>
                  {suffix}
                </Typography.Text>
              )}
            </Col>
          </Row>
        </Col>
        <Col span={errorCol}>
          {error && (
            <Typography.Text
              className="ant-form-item-explain ant-form-item-explain-error"
              type="danger"
              ellipsis={{ tooltip: true }}
              style={{ lineHeight: errorCol === 24 ? '16px' : '42px' }}
            >
              {error.message}
            </Typography.Text>
          )}
        </Col>
      </Row>
    </Form.Item>
  );
}

export default NegativeNumberField;
